import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Assignment,
  People,
  Settings,
  Logout,
  AccountCircle
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { UserType, ManagerRole } from '../../types/auth';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  title: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
}

const getNavItems = (userType: UserType, roles?: string[]): NavItem[] => {
  const baseItems: NavItem[] = [
    { title: 'Дашборд', icon: <Dashboard />, path: '/dashboard' },
  ];

  if (userType === UserType.USER) {
    return [
      ...baseItems,
      { title: 'Мои задачи', icon: <Assignment />, path: '/tasks' },
      { title: 'Профиль', icon: <AccountCircle />, path: '/profile' },
      { title: 'Настройки', icon: <Settings />, path: '/settings' },
    ];
  }

  if (userType === UserType.MANAGER) {
    const managerItems = [
      ...baseItems,
      { title: 'Задачи', icon: <Assignment />, path: '/tasks' },
    ];

    if (roles && (roles.includes(ManagerRole.ADMIN) || roles.includes(ManagerRole.CURATOR))) {
      managerItems.push({ title: 'Пользователи', icon: <People />, path: '/users' });
    }

    managerItems.push(
      { title: 'Профиль', icon: <AccountCircle />, path: '/profile' },
      { title: 'Настройки', icon: <Settings />, path: '/settings' }
    );

    return managerItems;
  }

  return baseItems;
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const { state, logout } = useAuth();
  const { isMobile } = useResponsive();
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const user = state.user;
  const navItems = user ? getNavItems(user.type, user.manager?.roles) : [];

  const getUserDisplayName = () => {
    if (user?.type === UserType.USER && user.user) {
      return `+${user.user.phone}`;
    }
    if (user?.type === UserType.MANAGER && user.manager) {
      return `+${user.manager.phone}`;
    }
    return 'Пользователь';
  };

  const getUserRole = () => {
    if (user?.type === UserType.USER) {
      return 'Исполнитель';
    }
    if (user?.type === UserType.MANAGER && user.manager?.roles) {
      const roles = user.manager.roles;
      if (roles.includes(ManagerRole.ADMIN)) return 'Администратор';
      if (roles.includes(ManagerRole.CURATOR)) return 'Куратор';
      if (roles.includes(ManagerRole.BUSINESS)) return 'Бизнес';
      if (roles.includes(ManagerRole.EDUCATION)) return 'Образование';
    }
    return 'Менеджер';
  };

  const drawerContent = (
    <Box sx={{ width: 250 }}>
      {/* Логотип и информация о пользователе */}
      <Box sx={{ p: 2, backgroundColor: theme.palette.primary.main, color: 'white' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          EAZZY
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, backgroundColor: 'rgba(255,255,255,0.2)' }}>
            {getUserDisplayName().charAt(1)}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {getUserDisplayName()}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {getUserRole()}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Навигация */}
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.path}
            component="a"
            href={item.path}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.title} />
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Выход */}
      <List>
        <ListItem
          component="button"
          onClick={handleLogout}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Выйти" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            EAZZY
          </Typography>

          {/* Desktop: Информация о пользователе */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {getUserDisplayName()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getUserRole()}
                </Typography>
              </Box>
              <IconButton onClick={handleProfileMenuOpen}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {getUserDisplayName().charAt(1)}
                </Avatar>
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? drawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Лучшая производительность на мобильных
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 250,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <AccountCircle />
          </ListItemIcon>
          Профиль
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          Настройки
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          Выйти
        </MenuItem>
      </Menu>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8, // Отступ для AppBar
          ml: isMobile ? 0 : '250px', // Отступ для Drawer на desktop
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
